/**
 * L9 wild masters (ยอดฝีมือพเนจรป่า): about one rare humanoid per region per hour of World time, placed with the injected
 * rng outside every safe zone, roaming. It is a `rival` with an arena-build body of a real art (data/wildMasters.ts), so
 * the fight is the duel's: the same `resolveHit` (guard blocks, guard break, wind-ups). Talking to it is the challenge
 * (it fights back for a while). Beaten, it pays a mastery mark (เครื่องหมายเชิงยุทธ์) of its art, qi into the pool and
 * silver. A mark raises that art's moves by one level (capped) at once when the art is learned, else it is kept to trade.
 */
import { arenaBody } from '../arena';
import { MAX_SKILL_LEVEL } from '../cultivation';
import { safeZoneAt } from '../data/safeZones';
import { placeOf, type RegionId } from '../data/regions';
import { getArt, getSkill } from '../data/skills';
import { WILD_MASTER, WILD_MASTERS, type WildMasterDef } from '../data/wildMasters';
import type { ZoneId } from '../data/zones';
import { freeAt } from '../path';
import { mulberry32, type Rng } from '../rng';
import { dist, type Entity, type WorldEvent } from '../world';
import type { SystemHost, WorldSystem } from './types';

declare module '../world' {
  interface Entity {
    /** A wild master (L9): its def id (data/wildMasters.ts). */
    wildMaster?: string;
  }
}

/** The masters that may roam a region. */
export const mastersOf = (region: RegionId): WildMasterDef[] => WILD_MASTERS.filter((m) => m.region === region);

export class WildMastersSystem implements WorldSystem {
  readonly name = 'wildMasters';
  /** Mastery marks held, by art id. */
  readonly marks: Record<string, number> = {};
  /** The live master's entity id, or -1. */
  aliveId = -1;
  /** World time of the next spawn. */
  nextAt = -1;
  spawned = 0;
  private done = false;
  /** Its own stream (from the map's seed), so the World's Rng draws in every other system stay as they were. */
  private rng: Rng | null = null;
  private random(): number {
    return (this.rng ??= mulberry32((this.h.world.map.seed ^ 0x57d3a1) >>> 0))();
  }
  constructor(private readonly h: SystemHost) {}

  region(): RegionId {
    return placeOf(this.h.world.map.zone as ZoneId)?.region ?? 'centre';
  }

  setup(): void {
    this.nextAt = this.random() * WILD_MASTER.hourS;
  }

  /** The live master, or null. */
  alive(): Entity | null {
    const e = this.h.world.entities.get(this.aliveId);
    return e && !e.dead ? e : null;
  }

  /** A free spot outside every safe zone, or null. */
  private spot(): { x: number; y: number } | null {
    const w = this.h.world;
    for (let i = 0; i < WILD_MASTER.tries; i++) {
      const x = 4 + this.random() * (w.map.w - 8);
      const y = 4 + this.random() * (w.map.h - 8);
      if (!freeAt(w.map, x, y) || safeZoneAt(w.map.zone, x, y)) continue;
      if (dist({ x, y }, w.player) < 24) continue;
      return { x, y };
    }
    return null;
  }

  spawn(): Entity | null {
    const w = this.h.world;
    const pool = mastersOf(this.region());
    if (!pool.length || w.map.interior) return null;
    const def = pool[Math.floor(this.random() * pool.length)]!;
    const at = this.spot();
    if (!at) return null;
    const e = this.h.add({
      kind: 'rival',
      x: at.x,
      y: at.y,
      name: def.name,
      title: def.title,
      sprite: def.sprite,
      wildMaster: def.id,
      rival: {
        homeX: at.x,
        homeY: at.y,
        aggroT: 0,
        skillT: 1.5,
        skills: getArt(def.art)
          .skills.filter((id) => ['attack', 'break', 'power'].includes(getSkill(id).kind))
          .slice(0, 4),
      },
    });
    Object.assign(
      e,
      arenaBody({
        name: def.name,
        title: def.title,
        sprite: def.sprite,
        art: def.art,
        skillLevel: def.skillLevel,
        neigong: def.neigong,
        neigongLevel: def.neigongLevel,
        equipment: {},
      }),
    );
    this.aliveId = e.id;
    this.done = false;
    this.spawned++;
    return e;
  }

  step(): void {
    const w = this.h.world;
    if (this.done) {
      this.done = false;
      w.entities.delete(this.aliveId);
      this.aliveId = -1;
    }
    if (this.nextAt < 0 || this.aliveId >= 0 || w.time < this.nextAt) return;
    // A window of 0.75-1.25 hours: about one per hour.
    this.nextAt = w.time + WILD_MASTER.hourS * (0.75 + 0.5 * this.random());
    this.spawn();
  }

  /** Talking is the challenge: it fights back for a minute (duel rules: guard, guard break, wind-ups). */
  talk(npc: Entity): boolean {
    if (!npc.wildMaster || npc.id !== this.aliveId || !npc.rival) return false;
    npc.rival.aggroT = 60;
    npc.pk = true;
    this.h.emit({ type: 'bark', id: npc.id, text: 'ดี! ให้ข้าดูฝีมือเจ้า' });
    this.h.emit({ type: 'dialog', npcId: npc.id, text: `${npc.name} (${npc.title}) รับคำท้า · สู้กันตามกติกาประลอง ระวังการ์ดแตก`, action: null });
    return true;
  }

  observe(e: WorldEvent): void {
    if (e.type !== 'death' || e.id !== this.aliveId) return;
    const w = this.h.world;
    const m = w.entities.get(e.id);
    if (!m?.wildMaster || w.player.dead) return;
    const def = WILD_MASTERS.find((d) => d.id === m.wildMaster);
    if (!def) return;
    this.done = true;
    this.marks[def.art] = (this.marks[def.art] ?? 0) + 1;
    w.sys.qiPool.add(WILD_MASTER.qi);
    w.progress.gold += WILD_MASTER.silver;
    const used = w.progress.arts.includes(def.art) && this.useMark(def.art);
    this.h.notice(
      `ชนะ${def.name}: เครื่องหมายเชิงยุทธ์ ${getArt(def.art).name}${used ? ' (เพิ่มระดับท่าแล้ว)' : ' (เก็บไว้แลก)'} · ${WILD_MASTER.qi} ลมปราณ · ${WILD_MASTER.silver} ตำลึง`,
    );
    this.h.emit({ type: 'progressChanged' });
  }

  /** Spends one mark: every move of the art gains levels (capped). False when none or nothing to raise. */
  useMark(art: string): boolean {
    if ((this.marks[art] ?? 0) <= 0) return false;
    const lv = this.h.world.progress.skillLevels;
    let raised = false;
    for (const id of getArt(art).skills) {
      const cur = lv[id] ?? 1;
      if (cur < MAX_SKILL_LEVEL) {
        lv[id] = Math.min(MAX_SKILL_LEVEL, cur + WILD_MASTER.markLevels);
        raised = true;
      }
    }
    if (raised) this.marks[art]!--;
    return raised;
  }

  /** The live master's place on the M map (metres in the region), or null. */
  mapMark(): { x: number; y: number; name: string } | null {
    const m = this.alive();
    return m ? { x: m.x, y: m.y, name: m.name } : null;
  }
}
